import {
  selectAppData,
  selectLocalPeerID,
  selectPeers,
  selectPeersScreenSharing,
  useHMSStore,
} from "@100mslive/react-sdk";
import { UserRound } from "lucide-react";
import { ScreenShareTile, VideoTile } from "@/components/meeting/VideoTile";
import { isInternalRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

const FirstPerson = () => (
  <div
    className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-stage-foreground"
    data-testid="first_person_img"
  >
    <div className="flex size-14 items-center justify-center rounded-full bg-white/10">
      <UserRound className="size-7" />
    </div>
    <p className="text-lg font-semibold">
      Welcome! You&apos;re the first one here
    </p>
    <p className="max-w-sm text-sm text-stage-foreground/70">
      The other side will appear here as soon as they join.
    </p>
  </div>
);

const gridColumns = count => {
  if (count <= 1) {
    return "grid-cols-1";
  }
  if (count <= 4) {
    return "grid-cols-1 sm:grid-cols-2";
  }
  return "grid-cols-2 lg:grid-cols-3";
};

/**
 * The video area. An interview is normally two people, so the other person
 * fills the stage and you sit in the corner; a screen share takes the stage
 * with everyone's cameras in a strip; more people get a grid.
 */
export const VideoStage = () => {
  const peers = useHMSStore(selectPeers).filter(
    peer => !isInternalRole(peer.roleName)
  );
  const localPeerId = useHMSStore(selectLocalPeerID);
  const sharingPeers = useHMSStore(selectPeersScreenSharing).filter(
    peer => !isInternalRole(peer.roleName)
  );
  const isAudioOnly = useHMSStore(selectAppData("isAudioOnly"));
  const remotePeers = peers.filter(peer => peer.id !== localPeerId);
  // No corner tile for an internal role (the recording bot, if it ever loads
  // this app): it is already left out of `peers`.
  const ownTileId = peers.some(peer => peer.id === localPeerId)
    ? localPeerId
    : null;

  if (sharingPeers.length > 0 && !isAudioOnly) {
    const [presenter] = sharingPeers;
    return (
      <div className="flex h-full flex-col gap-3 p-3 lg:flex-row">
        <ScreenShareTile peerId={presenter.id} className="min-h-0 flex-1" />
        <div className="flex shrink-0 gap-3 overflow-auto lg:w-56 lg:flex-col">
          {peers.map(peer => (
            <VideoTile
              key={peer.id}
              peerId={peer.id}
              className="aspect-video w-40 shrink-0 lg:w-full"
            />
          ))}
        </div>
      </div>
    );
  }

  if (remotePeers.length === 0) {
    return (
      <div className="relative h-full">
        <FirstPerson />
        {ownTileId ? (
          <VideoTile
            peerId={ownTileId}
            className="absolute right-3 bottom-3 aspect-video w-40 shadow-lg ring-1 ring-white/10 sm:w-56"
          />
        ) : null}
      </div>
    );
  }

  if (remotePeers.length === 1) {
    return (
      <div className="relative h-full p-3">
        <VideoTile peerId={remotePeers[0].id} className="h-full w-full" />
        {ownTileId ? (
          <VideoTile
            peerId={ownTileId}
            className="absolute right-6 bottom-6 aspect-video w-36 shadow-lg ring-1 ring-white/10 sm:w-56"
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid h-full auto-rows-fr gap-3 overflow-auto p-3",
        gridColumns(peers.length)
      )}
    >
      {peers.map(peer => (
        <VideoTile key={peer.id} peerId={peer.id} className="min-h-40" />
      ))}
    </div>
  );
};
